import { z } from 'zod';
/**
 * Shared types between Chrome extension and injection script
 */
// ===== Extension Signal Types =====
/**
 * Extension signal types for communication between injection script and Chrome extension
 */
export const ExtensionSignalTypeSchema = z.enum([
    'RPC_RESULT',
    'RPC_ERROR',
    'EXTENSION_ERROR',
    'CONSOLE_LOG_CAPTURED',
    'USER_ACTION',
    'EXTENSION_READY'
]);
/**
 * Base extension signal interface
 */
export const ExtensionSignalSchema = z.object({
    type: ExtensionSignalTypeSchema,
    pageId: z.string(),
    payload: z.unknown().optional(),
});
/**
 * Specific extension signal schemas
 */
export const ExtensionRpcResultSignalSchema = ExtensionSignalSchema.extend({
    type: z.literal('RPC_RESULT'),
    payload: z.object({
        requestId: z.string().optional(),
        rpcMethod: z.string(),
        result: z.unknown().optional(),
    }),
});
export const ExtensionRpcErrorSignalSchema = ExtensionSignalSchema.extend({
    type: z.literal('RPC_ERROR'),
    payload: z.object({
        requestId: z.string().optional(),
        rpcMethod: z.string(),
        error: z.string(),
    }),
});
export const ExtensionErrorSignalSchema = ExtensionSignalSchema.extend({
    type: z.literal('EXTENSION_ERROR'),
    payload: z.object({
        message: z.string(),
    }),
});
export const ExtensionReadySignalSchema = ExtensionSignalSchema.extend({
    type: z.literal('EXTENSION_READY'),
    payload: z.never().optional(),
});
/**
 * Type guards for extension signals
 */
export function isExtensionSignal(message) {
    return ExtensionSignalSchema.safeParse(message).success;
}
export function isExtensionRpcResultSignal(message) {
    return ExtensionRpcResultSignalSchema.safeParse(message).success;
}
export function isExtensionRpcErrorSignal(message) {
    return ExtensionRpcErrorSignalSchema.safeParse(message).success;
}
export function isExtensionErrorSignal(message) {
    return ExtensionErrorSignalSchema.safeParse(message).success;
}
export function isExtensionReadySignal(message) {
    return ExtensionReadySignalSchema.safeParse(message).success;
}
// ===== RPC Types =====
/**
 * RPC Data interface for protocol buffer communication
 */
export const RpcDataSchema = z.object({
    protoRequest: z.union([z.instanceof(Uint8Array), z.array(z.number())]),
    pageId: z.string(),
}).passthrough(); // Allow additional unknown properties
/**
 * RPC method names for outgoing requests to Go backend
 */
export const RpcMethodNameSchema = z.enum([
    'captureScreenshot',
    'captureElementScreenshot',
    'captureDOMElement',
    'captureAllText',
    'captureElementInnerText',
    'captureConsoleLogs',
    'captureSelectedText',
    'captureCode',
    'captureLogBlock'
]);
/**
 * Type guards for RPC types
 */
export function isRpcData(data) {
    return RpcDataSchema.safeParse(data).success;
}
export function isRpcMethodName(method) {
    return RpcMethodNameSchema.safeParse(method).success;
}
// ===== Injected Script to Extension Messages =====
/**
 * Log level enumeration for console capture
 */
export const LogLevelSchema = z.enum(["DEBUG", "INFO", "WARNING", "ERROR"]);
/**
 * Configuration object injected by Go into the parent_injection.js evaluate call.
 */
export const ParentInjectionConfigSchema = z.object({
    pageId: z.string(),
    extensionId: z.string().optional(),
    useExtension: z.boolean().optional(),
    languageServerUrl: z.string().optional(),
    csrfToken: z.string().optional(),
});
/**
 * Notification type constants for UI alerts
 */
export const NOTIFICATION_TYPES = {
    INFO: "info",
    SUCCESS: "success",
    WARNING: "warning",
    ERROR: "error",
};
/**
 * Notification type Zod schema for validation
 */
export const NotificationTypeSchema = z.enum([
    NOTIFICATION_TYPES.INFO,
    NOTIFICATION_TYPES.SUCCESS,
    NOTIFICATION_TYPES.WARNING,
    NOTIFICATION_TYPES.ERROR
]);
/**
 * Defines possible anchor points for UI elements
 */
export const AnchorPointSchema = z.enum([
    "top-left",
    "top-right",
    "bottom-right",
    "bottom-left"
]);
/**
 * Position configuration for floating UI elements
 */
export const AnchorPositionSchema = z.object({
    top: z.number(),
    right: z.number(),
    anchorPoint: AnchorPointSchema,
});
/**
 * Represents details of a captured DOM element
 */
export const DOMElementDataSchema = z.object({
    selector: z.string(),
    tagName: z.string(),
    id: z.string().optional(),
    outerHtml: z.string(),
    reactComponentName: z.string().optional(),
    reactSource: z.object({
        fileName: z.string().optional(),
        lineNumber: z.number().optional(),
        columnNumber: z.number().optional(),
    }).optional(),
    attributes: z.record(z.string(), z.string()),
    boundingBox: z.object({
        x: z.number(),
        y: z.number(),
        width: z.number(),
        height: z.number(),
        top: z.number(),
        left: z.number(),
        right: z.number(),
        bottom: z.number(),
    }),
    textContent: z.string(),
});
/**
 * Represents a captured console message
 */
export const ConsoleMessageDataSchema = z.object({
    level: LogLevelSchema,
    message: z.string(),
    timestamp: z.number(),
    url: z.string(),
});
export const ScreenshotOptionsSchema = z.object({
    selector: z.string().optional(),
    fullPage: z.boolean().optional(),
});
// ===== Messages FROM Injected Script TO Extension =====
/**
 * Signal types that the injected script can send to the extension
 */
export const InjectedScriptToExtensionSignalTypeSchema = z.enum([
    "INJECTED_SCRIPT_READY",
    "RPC_RESULT",
    "RPC_ERROR",
    "INJECTED_SCRIPT_ERROR",
    "CONSOLE_LOG_CAPTURED",
    "USER_ACTION"
]);
export const BaseInjectedScriptToExtensionSignalSchema = z.object({
    type: InjectedScriptToExtensionSignalTypeSchema,
    pageId: z.string(),
    payload: z.unknown().optional(),
});
export const InjectedScriptReadySignalSchema = BaseInjectedScriptToExtensionSignalSchema.extend({
    type: z.literal("INJECTED_SCRIPT_READY"),
    payload: z.never().optional(),
});
export const InjectedScriptRpcResultSignalSchema = BaseInjectedScriptToExtensionSignalSchema.extend({
    type: z.literal("RPC_RESULT"),
    payload: z.object({
        requestId: z.string(),
        rpcMethod: z.string(),
        result: z.unknown(),
    }),
});
export const InjectedScriptRpcErrorSignalSchema = BaseInjectedScriptToExtensionSignalSchema.extend({
    type: z.literal("RPC_ERROR"),
    payload: z.object({
        requestId: z.string(),
        rpcMethod: z.string(),
        error: z.string(),
    }),
});
export const InjectedScriptErrorSignalSchema = BaseInjectedScriptToExtensionSignalSchema.extend({
    type: z.literal("INJECTED_SCRIPT_ERROR"),
    payload: z.object({
        message: z.string(),
    }),
});
export const InjectedScriptConsoleLogCapturedSignalSchema = BaseInjectedScriptToExtensionSignalSchema.extend({
    type: z.literal("CONSOLE_LOG_CAPTURED"),
    payload: ConsoleMessageDataSchema,
});
export const InjectedScriptUserActionSignalSchema = BaseInjectedScriptToExtensionSignalSchema.extend({
    type: z.literal("USER_ACTION"),
    payload: z.object({
        actionType: z.string(),
        details: z.unknown(),
    }),
});
/**
 * Union schema for all signals the injected script can send to the extension
 */
export const InjectedScriptToExtensionMessageSchema = z.union([
    InjectedScriptReadySignalSchema,
    InjectedScriptRpcResultSignalSchema,
    InjectedScriptRpcErrorSignalSchema,
    InjectedScriptErrorSignalSchema,
    InjectedScriptConsoleLogCapturedSignalSchema,
    InjectedScriptUserActionSignalSchema,
]);
/**
 * Type guard for injected script to extension messages
 */
export function isInjectedScriptToExtensionMessage(message) {
    return InjectedScriptToExtensionMessageSchema.safeParse(message).success;
}
